/**
 * Beeldbellen: wat de cliëntweergave terugmeldt en hoe we dat aan de behandelaar tonen.
 * Er gaan alleen technische gegevens over de lijn, niets over de cliënt zelf.
 */

export type ViewerReport = {
  /** Cliënt heeft op «Ik ben er klaar voor» geklikt. */
  ready: boolean;
  /** Het tabblad met de bal staat vooraan. */
  visible: boolean;
  fullscreen: boolean;
  /** Gemeten beelden per seconde; null als er nog niet gemeten is. */
  fps: number | null;
};

export type ClientPresence = {
  /** Aantal verbonden cliëntweergaven. */
  count: number;
  /** Is er in deze sessie al eens een cliënt verbonden geweest? */
  everJoined: boolean;
  report: ViewerReport | null;
};

export type ClientStatusLevel = "waiting" | "ok" | "warn" | "lost";

export type ClientStatus = {
  level: ClientStatusLevel;
  title: string;
  detail: string;
};

/** Onder deze framerate oogt de beweging niet meer vloeiend. */
export const SMOOTH_FPS_MIN = 40;

export function isViewerReport(value: unknown): value is ViewerReport {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.ready === "boolean" &&
    typeof v.visible === "boolean" &&
    typeof v.fullscreen === "boolean" &&
    (v.fps === null || typeof v.fps === "number")
  );
}

export function describeClientStatus(presence: ClientPresence): ClientStatus {
  const { count, everJoined, report } = presence;

  if (count === 0) {
    if (everJoined) {
      return {
        level: "lost",
        title: "Verbinding met je cliënt verbroken",
        detail:
          "De bal loopt bij je cliënt door. Via het beeldbellen horen jullie elkaar nog.",
      };
    }
    return {
      level: "waiting",
      title: "Wachten op je cliënt",
      detail: "Zodra je cliënt de link opent, zie je dat hier.",
    };
  }

  if (!report || !report.ready) {
    return {
      level: "waiting",
      title: "Je cliënt heeft de link geopend",
      detail: "Je cliënt moet nog aangeven er klaar voor te zijn, met «Ik ben er klaar voor».",
    };
  }

  if (!report.visible) {
    return {
      level: "warn",
      title: "Je cliënt ziet de bal nu niet",
      detail:
        "Er staat een ander tabblad of venster voor. Vraag je cliënt terug te gaan naar het tabblad «EMDR».",
    };
  }

  if (report.fps !== null && report.fps < SMOOTH_FPS_MIN) {
    return {
      level: "warn",
      title: "De bal hapert bij je cliënt",
      detail:
        "Het apparaat van je cliënt is zwaar belast. Vraag je cliënt andere programma's en tabbladen te sluiten.",
    };
  }

  if (!report.fullscreen) {
    return {
      level: "ok",
      title: "Je cliënt is klaar",
      detail: "De bal is zichtbaar, maar niet op volledig scherm.",
    };
  }

  return {
    level: "ok",
    title: "Je cliënt is klaar",
    detail: "De bal is zichtbaar op volledig scherm en loopt vloeiend.",
  };
}

/** Kant-en-klare tekst om in de chat van het beeldbellen te plakken. */
export function invitationText(link: string): string {
  return `Open deze link voor de EMDR. Er opent een nieuw tabblad; kies daar «Ik ben er klaar voor».\n${link}`;
}

const CODE_PATTERN = /^[A-Z0-9]{6}$/;

/** Link voor de cliënt: alleen de kijkcode, geen token of andere parameters. */
export function clientLink(baseHref: string, viewerCode: string): string {
  const url = new URL(baseHref);
  url.search = "";
  url.hash = "";
  url.searchParams.set("sessie", viewerCode);
  return url.toString();
}

export function normalizeSessionCode(raw: string | null): string | null {
  const code = (raw ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
  return CODE_PATTERN.test(code) ? code : null;
}
